import { Color3, Mesh, MeshBuilder, Vector3 } from '@babylonjs/core';
import { createArenaScene, createRobotMesh, mat } from './arena';
import type { ArenaScene } from './arena';
import type { RobotValues, RulePack, SimCallbacks, SimEvent } from '../types';

function num(v: number | string | boolean | undefined, fallback: number): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/** Deterministic LCG so every player gets the same maze. */
function makeRng(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) % 4294967296;
    return s / 4294967296;
  };
}

// Directions: 0 = +Z (N), 1 = +X (E), 2 = -Z (S), 3 = -X (W)
const DX = [0, 1, 0, -1];
const DZ = [1, 0, -1, 0];

interface MazeData {
  size: number;
  /** walls[z][x] = bitmask of walls (1<<dir) around cell (x,z) */
  walls: number[][];
}

/** Recursive-backtracker maze: perfect maze, always solvable. */
function generateMaze(size: number): MazeData {
  const walls = Array.from({ length: size }, () => Array.from({ length: size }, () => 0b1111));
  const visited = Array.from({ length: size }, () => Array.from({ length: size }, () => false));
  const rng = makeRng(20260612);
  const stack: [number, number][] = [[0, 0]];
  visited[0][0] = true;
  while (stack.length) {
    const [x, z] = stack[stack.length - 1];
    const options: number[] = [];
    for (let d = 0; d < 4; d++) {
      const nx = x + DX[d];
      const nz = z + DZ[d];
      if (nx >= 0 && nx < size && nz >= 0 && nz < size && !visited[nz][nx]) options.push(d);
    }
    if (!options.length) {
      stack.pop();
      continue;
    }
    const d = options[Math.floor(rng() * options.length)];
    const nx = x + DX[d];
    const nz = z + DZ[d];
    walls[z][x] &= ~(1 << d);
    walls[nz][nx] &= ~(1 << ((d + 2) % 4));
    visited[nz][nx] = true;
    stack.push([nx, nz]);
  }
  return { size, walls };
}

/** BFS shortest path from start to goal (list of cells including both ends). */
function bfsPath(maze: MazeData, start: [number, number], goal: [number, number]): [number, number][] {
  const { size, walls } = maze;
  const prev = new Map<number, number>();
  const key = (x: number, z: number) => z * size + x;
  const queue: [number, number][] = [start];
  const seen = new Set<number>([key(...start)]);
  while (queue.length) {
    const [x, z] = queue.shift()!;
    if (x === goal[0] && z === goal[1]) break;
    for (let d = 0; d < 4; d++) {
      if (walls[z][x] & (1 << d)) continue;
      const nx = x + DX[d];
      const nz = z + DZ[d];
      if (seen.has(key(nx, nz))) continue;
      seen.add(key(nx, nz));
      prev.set(key(nx, nz), key(x, z));
      queue.push([nx, nz]);
    }
  }
  const path: [number, number][] = [];
  let cur = key(...goal);
  while (cur !== key(...start)) {
    path.unshift([cur % size, Math.floor(cur / size)]);
    const p = prev.get(cur);
    if (p === undefined) return [];
    cur = p;
  }
  path.unshift(start);
  return path;
}

type Phase = 'think' | 'turn' | 'move' | 'collide';

/** Kinematic maze-solver sim: wall-following or flood-fill on a generated grid maze. */
export class MazeSim {
  private arena: ArenaScene | null = null;
  private disposed = false;
  private finished = false;
  private events: SimEvent[] = [];
  private speedSeries: number[] = [];
  private time = 0;

  private canvas: HTMLCanvasElement;
  private pack: RulePack;
  private cfg: RobotValues;
  private cb: SimCallbacks;

  private maze: MazeData;
  private cell = 0.3;
  private robot: Mesh | null = null;
  private x = 0;
  private z = 0;
  private dir = 0;
  private phase: Phase = 'think';
  private phaseT = 0;
  private phaseDur = 0;
  private fromYaw = 0;
  private toYaw = 0;
  private fromPos = new Vector3();
  private toPos = new Vector3();
  private steps = 0;
  private collisions = 0;
  private visited = new Set<string>();
  private floodPath: [number, number][] = [];
  private floodIdx = 0;
  private rng = makeRng(987654321);
  private hudAccum = 0;
  private goal: [number, number];

  constructor(canvas: HTMLCanvasElement, pack: RulePack, cfg: RobotValues, cb: SimCallbacks) {
    this.canvas = canvas;
    this.pack = pack;
    this.cfg = cfg;
    this.cb = cb;
    const size = Math.max(4, Math.min(12, num(pack.maze?.gridSize, 8)));
    this.maze = generateMaze(size);
    this.goal = [size - 1, size - 1];
  }

  private cellPos(x: number, z: number): Vector3 {
    const half = (this.maze.size - 1) / 2;
    return new Vector3((x - half) * this.cell, 0.045, (z - half) * this.cell);
  }

  async start(): Promise<void> {
    const arena = createArenaScene(this.canvas, this.maze.size * 0.45);
    this.arena = arena;
    const { scene, camera } = arena;
    camera.beta = 0.55;

    const sizeM = this.maze.size * this.cell;
    const floor = MeshBuilder.CreateGround('mfloor', { width: sizeM + 0.2, height: sizeM + 0.2 }, scene);
    floor.material = mat(scene, 'mfloor-mat', new Color3(0.08, 0.1, 0.16));

    /* walls */
    const wallMat = mat(scene, 'mwall-mat', new Color3(0.3, 0.5, 0.9), 0.15);
    const wallH = num(this.pack.maze?.wallHeightCm, 15) / 100;
    const half = (this.maze.size - 1) / 2;
    for (let z = 0; z < this.maze.size; z++) {
      for (let x = 0; x < this.maze.size; x++) {
        const w = this.maze.walls[z][x];
        // build N and E walls per cell (+ S/W on boundary) to avoid duplicates
        const defs: [number, number, number, boolean][] = [
          [0, 0, 0.5, (w & 0b0001) !== 0], // N: north wall sits at +Z edge of cell
          [1, 0.5, 0, (w & 0b0010) !== 0], // E: east wall sits at +X edge of cell
        ];
        if (z === 0) defs.push([2, 0, -0.5, (w & 0b0100) !== 0]);
        if (x === 0) defs.push([3, -0.5, 0, (w & 0b1000) !== 0]);
        for (const [d, ox, oz2] of defs.filter((def) => def[3]).map((def) => [def[0], def[1], def[2]] as [number, number, number])) {
          const horizontal = d === 0 || d === 2;
          const wall = MeshBuilder.CreateBox(`mw-${x}-${z}-${d}`, {
            width: horizontal ? this.cell + 0.02 : 0.02,
            depth: horizontal ? 0.02 : this.cell + 0.02,
            height: wallH,
          }, scene);
          wall.position.set((x - half) * this.cell + ox * this.cell, wallH / 2, (z - half) * this.cell + oz2 * this.cell);
          wall.material = wallMat;
        }
      }
    }

    /* start + goal markers */
    const startM = MeshBuilder.CreateGround('mstart', { width: this.cell * 0.7, height: this.cell * 0.7 }, scene);
    startM.position = this.cellPos(0, 0);
    startM.position.y = 0.004;
    startM.material = mat(scene, 'mstart-mat', new Color3(0.1, 0.5, 1), 0.5);
    const goalM = MeshBuilder.CreateGround('mgoal', { width: this.cell * 0.7, height: this.cell * 0.7 }, scene);
    goalM.position = this.cellPos(...this.goal);
    goalM.position.y = 0.004;
    goalM.material = mat(scene, 'mgoal-mat', new Color3(0.1, 0.9, 0.4), 0.6);

    /* robot */
    this.robot = createRobotMesh(scene, 'mz', { color: new Color3(0.1, 0.9, 0.5), width: 0.13, length: 0.15, height: 0.07 });
    this.robot.position = this.cellPos(0, 0);
    this.visited.add('0,0');

    const algorithm = String(this.cfg.algorithm ?? 'Left-hand rule');
    if (algorithm.startsWith('Flood')) {
      this.floodPath = bfsPath(this.maze, [0, 0], this.goal);
      this.event('info', `Flood fill planned an optimal ${this.floodPath.length - 1}-step path`);
    } else {
      this.event('info', `Exploring with the ${algorithm.toLowerCase()}`);
    }

    arena.scene.onBeforeRenderObservable.add(() => {
      if (this.finished || this.disposed) return;
      const dt = Math.min(arena.engine.getDeltaTime() / 1000, 0.05);
      this.step(dt);
    });
  }

  private hasWall(x: number, z: number, d: number): boolean {
    return (this.maze.walls[z][x] & (1 << d)) !== 0;
  }

  private decide(): number {
    const algorithm = String(this.cfg.algorithm ?? 'Left-hand rule');
    if (algorithm.startsWith('Flood')) {
      this.floodIdx = Math.min(this.floodIdx + 1, this.floodPath.length - 1);
      const [nx, nz] = this.floodPath[this.floodIdx];
      for (let d = 0; d < 4; d++) {
        if (this.x + DX[d] === nx && this.z + DZ[d] === nz) return d;
      }
      return this.dir;
    }
    const left = algorithm.startsWith('Left');
    const order = left
      ? [(this.dir + 3) % 4, this.dir, (this.dir + 1) % 4, (this.dir + 2) % 4]
      : [(this.dir + 1) % 4, this.dir, (this.dir + 3) % 4, (this.dir + 2) % 4];
    for (const d of order) {
      if (!this.hasWall(this.x, this.z, d)) return d;
    }
    return (this.dir + 2) % 4;
  }

  private step(dt: number): void {
    this.time += dt;
    const speed = num(this.cfg.speed, 0.5);
    const cal = num(this.cfg.turnCalibration, 0.8);
    const noisy = String(this.cfg.sensorNoise ?? '').startsWith('Ultrasonic');
    const maxTime = num(this.pack.match?.maxTimeSec, 180);

    if (this.time > maxTime) {
      this.finish(false, 'Time limit exceeded');
      return;
    }

    this.phaseT += dt;

    if (this.phase === 'think') {
      const target = this.decide();
      if (target !== this.dir) {
        this.phase = 'turn';
        this.fromYaw = this.dirYaw(this.dir);
        let delta = this.dirYaw(target) - this.fromYaw;
        while (delta > Math.PI) delta -= 2 * Math.PI;
        while (delta < -Math.PI) delta += 2 * Math.PI;
        this.toYaw = this.fromYaw + delta;
        this.phaseDur = (Math.abs(delta) / (Math.PI / 2)) * 0.35;
        this.dir = target;
        // badly calibrated turns sometimes clip a wall
        const collisionChance = (1 - cal) * 0.4 + (noisy ? 0.04 : 0);
        if (this.rng() < collisionChance) {
          this.collisions++;
          this.event('warn', `Wall collision on turn at ${this.time.toFixed(1)}s (turn calibration ${cal.toFixed(2)})`);
          this.phase = 'collide';
          this.phaseDur = 0.8;
        }
      } else {
        this.beginMove(speed);
      }
      this.phaseT = 0;
    } else if (this.phase === 'turn') {
      const k = Math.min(1, this.phaseT / Math.max(this.phaseDur, 0.01));
      if (this.robot) this.robot.rotation = new Vector3(0, this.fromYaw + (this.toYaw - this.fromYaw) * k, 0);
      if (k >= 1) {
        this.beginMove(speed);
        this.phaseT = 0;
      }
    } else if (this.phase === 'collide') {
      // wobble in place
      if (this.robot) this.robot.rotation = new Vector3(0, this.dirYaw(this.dir) + Math.sin(this.phaseT * 30) * 0.1, 0);
      if (this.phaseT >= this.phaseDur) {
        this.beginMove(speed);
        this.phaseT = 0;
      }
    } else if (this.phase === 'move') {
      const k = Math.min(1, this.phaseT / Math.max(this.phaseDur, 0.01));
      if (this.robot) this.robot.position = Vector3.Lerp(this.fromPos, this.toPos, k);
      if (k >= 1) {
        this.x += DX[this.dir];
        this.z += DZ[this.dir];
        this.steps++;
        this.visited.add(`${this.x},${this.z}`);
        if (this.x === this.goal[0] && this.z === this.goal[1]) {
          this.finish(true, 'Finish cell reached!');
          return;
        }
        if (this.steps > this.maze.size * this.maze.size * 6) {
          this.finish(false, 'Robot got stuck exploring');
          return;
        }
        this.phase = 'think';
        this.phaseT = 0;
      }
    }

    this.speedSeries.push(this.phase === 'move' ? speed : 0);
    if (this.speedSeries.length > 4000) this.speedSeries.shift();

    this.hudAccum += dt;
    if (this.hudAccum >= 0.15) {
      this.hudAccum = 0;
      this.cb.onTick({
        timeSec: this.time,
        status: this.phase.toUpperCase(),
        sensors: {
          'Front wall': this.hasWall(this.x, this.z, this.dir) ? 'YES' : 'open',
          'Left wall': this.hasWall(this.x, this.z, (this.dir + 3) % 4) ? 'YES' : 'open',
          'Right wall': this.hasWall(this.x, this.z, (this.dir + 1) % 4) ? 'YES' : 'open',
          Cell: `(${this.x}, ${this.z})`,
          Steps: `${this.steps}`,
          Visited: `${this.visited.size}/${this.maze.size * this.maze.size} cells`,
        },
      });
    }
  }

  private beginMove(speed: number): void {
    this.phase = 'move';
    this.fromPos = this.cellPos(this.x, this.z);
    this.toPos = this.cellPos(this.x + DX[this.dir], this.z + DZ[this.dir]);
    this.phaseDur = this.cell / Math.max(speed, 0.05);
    if (this.robot) this.robot.rotation = new Vector3(0, this.dirYaw(this.dir), 0);
  }

  private dirYaw(d: number): number {
    return [0, Math.PI / 2, Math.PI, -Math.PI / 2][d];
  }

  private finish(won: boolean, outcome: string): void {
    if (this.finished) return;
    this.finished = true;
    const sc = this.pack.scoring ?? {};
    const optimal = Math.max(1, bfsPath(this.maze, [0, 0], this.goal).length - 1);
    const efficiency = won ? optimal / Math.max(this.steps, 1) : 0;
    const maxTime = num(this.pack.match?.maxTimeSec, 180);

    let score = 0;
    if (won) {
      score += num(sc.finishBase, 100);
      score += Math.round((1 - Math.min(this.time / maxTime, 1)) * num(sc.timeBonusMax, 50));
      score += Math.round(efficiency * num(sc.efficiencyBonusMax, 50));
    } else if (outcome.includes('stuck')) {
      score -= num(sc.stuckPenalty, 20);
    }
    score -= this.collisions * num(sc.wallCollisionPenalty, 10);

    this.event(won ? 'good' : 'bad', outcome);
    this.cb.onEnd({
      competition: 'maze',
      outcome,
      won,
      score,
      timeSec: this.time,
      metrics: {
        Time: `${this.time.toFixed(1)} s`,
        'Steps taken': `${this.steps}`,
        'Optimal path': `${optimal} steps`,
        'Path efficiency': `${Math.round(efficiency * 100)}%`,
        'Wall collisions': `${this.collisions}`,
        'Cells explored': `${this.visited.size}`,
      },
      raw: { collisions: this.collisions, efficiency, steps: this.steps, optimal, time: this.time },
      events: this.events,
      speedSeries: this.speedSeries.filter((_, i) => i % 6 === 0),
    });
  }

  private event(type: SimEvent['type'], msg: string): void {
    this.events.push({ t: this.time, type, msg });
  }

  dispose(): void {
    this.disposed = true;
    this.arena?.dispose();
    this.arena = null;
  }
}
